import AppConfig from '@/config'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { yandexMetrika } from '@/services/yandexMetrika'
import type { LimitKind } from '@/api/errors'
import type { UsageResource } from '@/stores/usage'

// Цели Метрики шлём только из b2c-сборок. Без согласия на cookies (или без счётчика)
// цель отбрасывается до дедупликации, чтобы не пометить «отправленной» то, что не ушло.

export type MetrikaGoal =
	| 'site_visit'
	| 'login_success'
	| 'signup_success'
	| 'limit_reached'
	| 'payment_init'
	| 'payment_success'
	| 'payment_failed'

export type GoalParams = Record<string, string>

export interface SendGoalOptions {
	/** Ключ дедупликации: цель с тем же ключом повторно не уходит. */
	dedupeKey: string
	/** tab — до закрытия вкладки (sessionStorage), forever — навсегда (localStorage). */
	dedupeFor: 'tab' | 'forever'
}

interface DeferredGoal {
	goal: MetrikaGoal
	params?: GoalParams
}

/** Сколько ключей помним: постоянное хранилище не должно расти бесконечно. */
const MAX_SENT_KEYS = 50

function storageFor(dedupeFor: SendGoalOptions['dedupeFor']): Storage {
	return dedupeFor === 'forever' ? localStorage : sessionStorage
}

function readList<T>(storage: Storage, key: string): T[] {
	try {
		const parsed = JSON.parse(storage.getItem(key) ?? '[]')
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

function send(goal: MetrikaGoal, params?: GoalParams): void {
	if (import.meta.env.DEV) console.debug('[ym goal]', goal, params)
	yandexMetrika.reachGoal(goal, params)
}

export function sendGoal(goal: MetrikaGoal, params?: GoalParams, options?: SendGoalOptions): void {
	if (!AppConfig.internalB2C || !yandexMetrika.canSend) return

	if (options) {
		const storage = storageFor(options.dedupeFor)
		const key = `${goal}:${options.dedupeKey}`
		const sent = readList<string>(storage, STORAGE_KEYS.METRIKA_GOALS_SENT)
		if (sent.includes(key)) return
		storage.setItem(STORAGE_KEYS.METRIKA_GOALS_SENT, JSON.stringify([...sent, key].slice(-MAX_SENT_KEYS)))
	}

	send(goal, params)
}

/** Отложить цель до следующей загрузки страницы — для целей прямо перед полной перезагрузкой,
 * когда reachGoal может не успеть уйти. Отправляет sendDeferredGoals после init счётчика. */
export function sendGoalAfterReload(goal: MetrikaGoal, params?: GoalParams): void {
	if (!AppConfig.internalB2C) return
	const deferred = readList<DeferredGoal>(sessionStorage, STORAGE_KEYS.METRIKA_GOALS_DEFERRED)
	sessionStorage.setItem(STORAGE_KEYS.METRIKA_GOALS_DEFERRED, JSON.stringify([...deferred, { goal, params }]))
}

export function sendDeferredGoals(): void {
	const deferred = readList<DeferredGoal>(sessionStorage, STORAGE_KEYS.METRIKA_GOALS_DEFERRED)
	// Очищаем до отправки: цель из очереди не должна уйти дважды.
	sessionStorage.removeItem(STORAGE_KEYS.METRIKA_GOALS_DEFERRED)
	deferred.forEach(({ goal, params }) => sendGoal(goal, params))
}

export function trackSiteVisit(): void {
	sendGoal('site_visit', undefined, { dedupeKey: 'visit', dedupeFor: 'tab' })
}

/** Успешный вход. Новый пользователь получает обе цели: login_success — любой вход,
 * signup_success выделяет регистрации. Отложено: после входа идёт полная перезагрузка.
 * Общая точка для всех способов входа (код на e-mail, будущий OAuth-callback). */
export function trackAuthSuccess(isNew: boolean): void {
	sendGoalAfterReload('login_success')
	if (isNew) sendGoalAfterReload('signup_success')
}

/** Исчерпана квота: гостевая или тарифная по ресурсу. Раз за сессию вкладки на пару
 * limit_type + resource — один и тот же лимит ловят и клиентский гейт, и сервер. */
export function trackLimitReached(limitType: LimitKind, resource: UsageResource = 'text'): void {
	if (limitType === 'guest') {
		sendGoal('limit_reached', { limit_type: 'guest' }, { dedupeKey: 'guest', dedupeFor: 'tab' })
		return
	}
	sendGoal('limit_reached', { limit_type: 'plan', resource }, { dedupeKey: `plan:${resource}`, dedupeFor: 'tab' })
}

/** Откуда начата оплата. */
export type PaymentSource = 'paywall' | 'plan_card' | 'suspended' | 'retry' | 'subscription' | 'onboarding'

/** Клик по оплате — не открытие виджета и не отправка карты, поэтому init > success + failed.
 * Без дедупликации: каждый клик — попытка. */
export function trackPaymentInit(source: PaymentSource): void {
	sendGoal('payment_init', { source })
}

/** Заказ оплачен по данным бэка. Раз на invoiceId: успех приходит и живым поллингом,
 * и restore() после перезагрузки, и проверкой перед отменой. */
export function trackPaymentSuccess(invoiceId: string): void {
	sendGoal('payment_success', undefined, { dedupeKey: invoiceId, dedupeFor: 'forever' })
}

/** Бэк вернул failed. Отмена пользователем — не неудача. reason — сырой код бэка,
 * в том числе незнакомый фронту. */
export function trackPaymentFailed(invoiceId: string, reason: string | null): void {
	if (reason === 'cancelled_by_user') return
	sendGoal('payment_failed', { reason: reason ?? 'none' }, { dedupeKey: invoiceId, dedupeFor: 'forever' })
}
